import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AnimateButton from "ui-component/extended/AnimateButton";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useBoundStore } from "useStore";
import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useAdsMutation from "../Queries/AdsMutation";
import { CmSendStatus } from "./CmSendStatus";
import { CmConfirmExitDialog } from "./CmConfirmExitDialog";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";

function getChangeType(row) {
  if (row._deleted) return "deleted";
  if (row.active?.valueEdited === true && row.active?.valueOriginal === false)
    return "activated";
  if (row.active?.valueEdited === false && row.active?.valueOriginal === true)
    return "deactivated";
  return null;
}

function getOriginalType(row) {
  if (row.active?.valueOriginal === true) return "activated";
  if (row.active?.valueOriginal === false) return "deactivated";
  if (!row.active?.valueOriginal && row.active === true) return "activated";
  if (!row.active?.valueOriginal && row.active === false) return "deactivated";
  return null;
}

const CHANGE_CONFIG = {
  activated: { label: "Activa", bg: "#A7F3D0", color: "#064E3B" },
  deactivated: { label: "Inactiva", bg: "#0000001F", color: "#00000061" },
  deleted: { label: "Eliminada", bg: "#FDEDED", color: "#C62828" },
};

function ChangeChip({ changeType }) {
  const cfg = CHANGE_CONFIG[changeType];
  if (!cfg) return null;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 400,
        fontSize: "14px",
      }}
    />
  );
}

const ActiveIcon = () => {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.5 6C11.6935 6 12.8381 6.47411 13.682 7.31802C14.5259 8.16193 15 9.30653 15 10.5C15 11.6935 14.5259 12.8381 13.682 13.682C12.8381 14.5259 11.6935 15 10.5 15C9.30653 15 8.16193 14.5259 7.31802 13.682C6.47411 12.8381 6 11.6935 6 10.5C6 9.30653 6.47411 8.16193 7.31802 7.31802C8.16193 6.47411 9.30653 6 10.5 6ZM12.854 9.146C12.7602 9.05226 12.6331 8.99961 12.5005 8.99961C12.3679 8.99961 12.2408 9.05226 12.147 9.146L10 11.294L8.854 10.148C8.80758 10.1015 8.75245 10.0646 8.69177 10.0394C8.6311 10.0143 8.56605 10.0013 8.50035 10.0012C8.43466 10.0012 8.36959 10.0141 8.30888 10.0392C8.24816 10.0643 8.19299 10.1011 8.1465 10.1475C8.10001 10.1939 8.06312 10.249 8.03794 10.3097C8.01275 10.3704 7.99977 10.4354 7.99972 10.5011C7.99968 10.5668 8.01257 10.6319 8.03767 10.6926C8.06277 10.7533 8.09958 10.8085 8.146 10.855L9.646 12.355C9.73976 12.4487 9.86692 12.5014 9.9995 12.5014C10.1321 12.5014 10.2592 12.4487 10.353 12.355L12.853 9.855C12.9467 9.76124 12.9994 9.63408 12.9994 9.5015C12.9994 9.36892 12.9467 9.24176 12.853 9.148"
        fill="black"
        fillOpacity="0.38"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11 0C11.7956 0 12.5587 0.31607 13.1213 0.87868C13.6839 1.44129 14 2.20435 14 3V6H13.6C13.403 5.865 13.214 5.717 13 5.607V4H1V11C1 11.5304 1.21071 12.0391 1.58579 12.4142C1.96086 12.7893 2.46957 13 3 13H5.607C5.717 13.215 5.865 13.403 6 13.6V14H3C2.20435 14 1.44129 13.6839 0.87868 13.1213C0.31607 12.5587 0 11.7956 0 11V3C0 2.20435 0.31607 1.44129 0.87868 0.87868C1.44129 0.31607 2.20435 0 3 0H11ZM3 1C2.46957 1 1.96086 1.21071 1.58579 1.58579C1.21071 1.96086 1 2.46957 1 3H13C13 2.46957 12.7893 1.96086 12.4142 1.58579C12.0391 1.21071 11.5304 1 11 1H3Z"
        fill="black"
        fillOpacity="0.38"
      />
    </svg>
  );
};

const DeleteIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_268_99820)">
        <path
          d="M3.15159 10.7569C2.12159 9.72619 1.60625 9.21153 1.41492 8.54286C1.22292 7.87419 1.38692 7.16419 1.71492 5.74486L1.90359 4.92619C2.17892 3.73153 2.31692 3.13419 2.72559 2.72486C3.13425 2.31553 3.73225 2.17819 4.92692 1.90286L5.74559 1.71353C7.16559 1.38619 7.87492 1.22219 8.54359 1.41353C9.21225 1.60553 9.72692 2.12086 10.7569 3.15086L11.9769 4.37086C13.7709 6.16419 14.6663 7.06019 14.6663 8.17353C14.6663 9.28753 13.7703 10.1835 11.9776 11.9762C10.1843 13.7695 9.28825 14.6655 8.17425 14.6655C7.06092 14.6655 6.16425 13.7695 4.37159 11.9769L3.15159 10.7569Z"
          stroke="black"
          strokeOpacity="0.38"
          strokeWidth="1.5"
        />
        <path
          d="M10.2599 10.259C10.6499 9.86767 10.7026 9.28767 10.3773 8.96167C10.0519 8.63567 9.47126 8.68901 9.08059 9.07967C8.69059 9.47034 8.10993 9.52301 7.78459 9.19767C7.45926 8.87234 7.51193 8.29167 7.90259 7.90167M7.90259 7.90167L7.66659 7.66567M7.90259 7.90167C8.12326 7.68034 8.40459 7.56834 8.66659 7.57034M10.4953 10.4943L10.2593 10.2583C9.99259 10.5257 9.63593 10.635 9.33326 10.5723"
          stroke="black"
          strokeOpacity="0.38"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M6.67999 6.86078C7.20069 6.34008 7.20069 5.49586 6.67999 4.97516C6.15929 4.45446 5.31507 4.45446 4.79437 4.97516C4.27367 5.49586 4.27367 6.34008 4.79437 6.86078C5.31507 7.38148 6.15929 7.38148 6.67999 6.86078Z"
          stroke="black"
          strokeOpacity="0.38"
          strokeWidth="1.5"
        />
      </g>
      <defs>
        <clipPath id="clip0_268_99820">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const DesactiveIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 1C12.7956 1 13.5587 1.31607 14.1213 1.87868C14.6839 2.44129 15 3.20435 15 4V7H14.6C14.403 6.865 14.214 6.717 14 6.607V5H2V12C2 12.5304 2.21071 13.0391 2.58579 13.4142C2.96086 13.7893 3.46957 14 4 14H6.607C6.717 14.215 6.865 14.403 7 14.6V15H4C3.20435 15 2.44129 14.6839 1.87868 14.1213C1.31607 13.5587 1 12.7956 1 12V4C1 3.20435 1.31607 2.44129 1.87868 1.87868C2.44129 1.31607 3.20435 1 4 1H12ZM4 2C3.46957 2 2.96086 2.21071 2.58579 2.58579C2.21071 2.96086 2 3.46957 2 4H14C14 3.46957 13.7893 2.96086 13.4142 2.58579C13.0391 2.21071 12.5304 2 12 2H4Z"
        fill="black"
        fillOpacity="0.38"
      />
      <g clipPath="url(#clip0_268_99810)">
        <rect
          x="7"
          y="7"
          width="9"
          height="9"
          rx="4.5"
          fill="black"
          fillOpacity="0.38"
        />
        <path
          d="M11.5 11.5L9.625 9.625M11.5 11.5L13.375 13.375M11.5 11.5L13.375 9.625M11.5 11.5L9.625 13.375"
          stroke="white"
          strokeWidth="0.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_268_99810">
          <rect x="7" y="7" width="9" height="9" rx="4.5" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const COL_WIDTH = { checkbox: 80, mla: 280, change: 600, send: 220 };

function AdsPublishSummary({ allModifiedKeys, selectedRows, adsEditedRows }) {
  const activatedCount = allModifiedKeys.filter(
    (k) => selectedRows[k] && getChangeType(adsEditedRows[k]) === "activated",
  ).length;
  const deactivatedCount = allModifiedKeys.filter(
    (k) => selectedRows[k] && getChangeType(adsEditedRows[k]) === "deactivated",
  ).length;
  const deletedCount = allModifiedKeys.filter(
    (k) => selectedRows[k] && getChangeType(adsEditedRows[k]) === "deleted",
  ).length;

  return (
    <Stack
      direction="column"
      spacing={2}
      padding={2}
      sx={{ border: "1px solid #A7A8A880", borderRadius: "8px" }}
    >
      <Typography sx={{ fontSize: 14, textDecoration: "underline" }}>
        <strong>Resumen</strong>
      </Typography>

      <Stack spacing={1}>
        {activatedCount > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <ActiveIcon />
            <Typography>Anuncios activados:</Typography>
            <strong>{activatedCount}</strong>
          </Stack>
        )}
        {deactivatedCount > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <DesactiveIcon />
            <Typography>Anuncios desactivados:</Typography>
            <strong>{deactivatedCount}</strong>
          </Stack>
        )}
        {deletedCount > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <DeleteIcon />
            <Typography>Anuncios eliminados:</Typography>
            <strong>{deletedCount}</strong>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>
          Total:
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>
          {activatedCount + deactivatedCount + deletedCount}
        </Typography>
      </Stack>
    </Stack>
  );
}

function AdsPublishTable({
  allModifiedKeys,
  selectedRows,
  adsEditedRows,
  handleRowSelectionChange,
  loading,
  sendState,
  feedbackByRow,
}) {
  const isDisabled = loading || sendState === "success";

  return (
    <>
      {allModifiedKeys.map((itemId) => {
        const row = adsEditedRows[itemId];
        const originalType = getOriginalType(row);
        const changeType = getChangeType(row);

        const feedback = feedbackByRow[itemId];
        const displayState = feedback
          ? feedback.success
            ? "success"
            : "error"
          : "pending";

        return (
          <Box
            key={itemId}
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#EEF1F6",
              borderLeft: "4px solid #002878",
              borderRadius: 1,
              py: 1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: COL_WIDTH.checkbox,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Checkbox
                checked={!!selectedRows[itemId]}
                onChange={() => handleRowSelectionChange(itemId)}
                disabled={isDisabled}
                sx={{ p: 0, "&.Mui-checked": { color: "#002878" } }}
              />
            </Box>

            <Box sx={{ width: COL_WIDTH.mla }}>
              <Typography
                fontWeight={600}
                fontSize="16px"
                sx={{ color: "#000000DE" }}
              >
                {itemId}
              </Typography>
            </Box>

            <Box
              sx={{ width: COL_WIDTH.change }}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Typography>Estado:</Typography>
              <ChangeChip changeType={originalType} />
              <ArrowForwardOutlinedIcon fontSize="small" />
              <ChangeChip changeType={changeType} />
            </Box>

            <Box
              sx={{
                width: COL_WIDTH.send,
                display: "flex",
                alignItems: "center",
              }}
            >
              {selectedRows[itemId] && (
                <CmSendStatus
                  send={displayState}
                  loading={loading}
                  message={feedback?.message}
                  isChecked={!!selectedRows[itemId]}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </>
  );
}

/* ---------- Main button ---------- */

export default function AdsPublishButton() {
  const adsEditedRows = useBoundStore((state) => state.adsEditedRows);
  const adsSelectionModel = useBoundStore((state) => state.adsSelectionModel);
  const clearAdsEditedRows = useBoundStore((state) => state.clearAdsEditedRows);
  const setAdsSelectionModel = useBoundStore(
    (state) => state.setAdsSelectionModel,
  );

  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendState, setSendState] = useState("pending");
  const [feedbackByRow, setFeedbackByRow] = useState({});
  const [selectedRows, setSelectedRows] = useState({});

  const mutation = useAdsMutation();
  const queryClient = useQueryClient();

  const allModifiedKeys = useMemo(
    () =>
      Object.keys(adsEditedRows).filter((itemId) => {
        const row = adsEditedRows[itemId];
        if (row._deleted) return true;
        if (row.active === undefined) return false;
        return row.active.valueEdited !== row.active.valueOriginal;
      }),
    [adsEditedRows],
  );

  const isDisabled = allModifiedKeys.length === 0;
  const totalCount = allModifiedKeys.length;

  const handleOpen = () => {
    const initial = {};
    allModifiedKeys.forEach((key) => {
      initial[key] = adsSelectionModel.includes(key);
    });
    setSelectedRows(initial);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setOpenModal(false);
    if (sendState === "success") {
      clearAdsEditedRows();
      setAdsSelectionModel([]);
      setSendState("pending");
      setFeedbackByRow({});
      setSelectedRows({});
      queryClient.invalidateQueries({ queryKey: ["campaignData"] });
    }
  };

  const toggleRow = (key) =>
    setSelectedRows((prev) => ({ ...prev, [key]: !prev[key] }));

  const allSelected =
    allModifiedKeys.length > 0 && allModifiedKeys.every((k) => selectedRows[k]);
  const someSelected =
    allModifiedKeys.some((k) => selectedRows[k]) && !allSelected;

  const toggleAll = () => {
    const newVal = !allSelected;
    const next = {};
    allModifiedKeys.forEach((k) => (next[k] = newVal));
    setSelectedRows(next);
  };

  const hasSelectedRows = allModifiedKeys.some((k) => selectedRows[k]);

  const handleSubmit = () => {
    const payload = allModifiedKeys
      .filter((itemId) => selectedRows[itemId])
      .map((itemId) => {
        const row = adsEditedRows[itemId];
        return {
          itemId,
          ...(row._deleted
            ? { deleted: true }
            : { active: row.active?.valueEdited }),
        };
      });

    setLoading(true);
    mutation.mutate(payload, {
      onSuccess: (response = []) => {
        const feedbackMap = {};
        if (Array.isArray(response)) {
          response.forEach((r) => {
            feedbackMap[r.itemId] = {
              success: !r.isError,
              message: r.message,
            };
          });
        } else {
          payload.forEach(({ itemId }) => {
            feedbackMap[itemId] = { success: true };
          });
        }
        setFeedbackByRow(feedbackMap);
        setSendState("success");
      },
      onError: () => setSendState("error"),
      onSettled: () => setLoading(false),
    });
  };

  return (
    <Stack direction="row" gap={2} alignItems="stretch">
      <AnimateButton style={{ width: "100%", display: "flex" }}>
        <Button
          fullWidth
          style={{
            height: "100%",
            backgroundColor: isDisabled ? "#0000001F" : "#002878",
            "&:hover": { backgroundColor: "#002878" },
          }}
          endIcon={<UploadFileIcon />}
          onClick={handleOpen}
          variant="contained"
          disabled={isDisabled}
        >
          Publicar
        </Button>
      </AnimateButton>

      <Dialog
        open={open}
        onClose={loading ? undefined : handleClose}
        maxWidth="lg"
        fullWidth
      >
        <Stack direction="column" spacing={2} padding={2}>
          <DialogTitle sx={{ backgroundColor: "#E5E7EB" }}>
            {sendState === "success"
              ? "Modificaciones enviadas"
              : "Confirma las modificaciones realizadas"}
          </DialogTitle>
        </Stack>

        <DialogContent>
          <AdsPublishSummary
            allModifiedKeys={allModifiedKeys}
            selectedRows={selectedRows}
            adsEditedRows={adsEditedRows}
          />

          <Divider sx={{ marginY: 2 }} />

          <AdsPublishTable
            allModifiedKeys={allModifiedKeys}
            selectedRows={selectedRows}
            adsEditedRows={adsEditedRows}
            handleRowSelectionChange={toggleRow}
            allSelected={allSelected}
            someSelected={someSelected}
            handleSelectAll={toggleAll}
            loading={loading}
            sendState={sendState}
            feedbackByRow={feedbackByRow}
          />
        </DialogContent>

        <DialogActions>
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            alignItems="center"
            paddingX={2}
          >
            {loading && sendState !== "success" && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ backgroundColor: "#E5F6FD", padding: 1, borderRadius: 1 }}
              >
                <ErrorOutlineOutlinedIcon sx={{ color: "#0288D1" }} />
                <Typography sx={{ color: "#014361", fontSize: "14px" }}>
                  Por favor no cerrar el modal ni refrescar la página.
                </Typography>
              </Stack>
            )}

            {sendState === "success" && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ backgroundColor: "#E5F6FD", padding: 1, borderRadius: 1 }}
              >
                <ErrorOutlineOutlinedIcon sx={{ color: "#0288D1" }} />
                <Stack direction="column">
                  <Typography
                    sx={{ color: "#014361", fontSize: "14px", fontWeight: 400 }}
                  >
                    Los cambios fueron enviados
                  </Typography>
                  <Typography sx={{ color: "#014361", fontSize: "12px" }}>
                    Puedes verificar el resultado en la tabla.
                  </Typography>
                </Stack>
              </Stack>
            )}

            <Button
              onClick={
                sendState === "success" ? handleClose : () => setOpenModal(true)
              }
              disabled={loading}
              variant="outline"
              sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
                fontWeight: 500,
                color: "#354887",
                height: "32px",
                textTransform: "uppercase",
                border: "1px solid #354887",
                "&:hover": {
                  backgroundColor: "#F5F5F5",
                  borderColor: "#2A3B6F",
                  color: "#2A3B6F",
                },
              }}
            >
              {sendState === "success" ? "Cerrar" : "Cancelar"}
            </Button>

            <CmConfirmExitDialog
              openModal={openModal}
              handleClose={handleClose}
              handleClickClose={() => setOpenModal(false)}
              totalCount={totalCount}
            />

            <Button
              onClick={handleSubmit}
              disabled={!hasSelectedRows || loading || sendState === "success"}
              variant="contained"
              sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
                fontWeight: 500,
                color: "#ffffff",
                backgroundColor: hasSelectedRows ? "#354887" : "#0000001F",
                height: "32px",
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: hasSelectedRows ? "#2c3b6f" : "#0000001F",
                },
              }}
            >
              {loading && sendState === "pending" ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography
                    sx={{ color: "rgba(0,0,0,0.38)", fontSize: "14px" }}
                  >
                    Publicando...
                  </Typography>
                  <CircularProgress sx={{ color: "#8B8B8B" }} size={18} />
                </Box>
              ) : sendState === "success" ? (
                "Enviado"
              ) : sendState === "error" ? (
                "Error al enviar"
              ) : (
                "Enviar cambios"
              )}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
